import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useAuth } from '../App';
import PlanStatus from './PlanStatus';
import './Layout.css';

// Icons as simple SVG components
const DashboardIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="3" width="7" height="7" rx="1" />
        <rect x="14" y="3" width="7" height="7" rx="1" />
        <rect x="3" y="14" width="7" height="7" rx="1" />
        <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
);

const PortalIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
        <line x1="2" y1="10" x2="22" y2="10" />
        <line x1="7" y1="21" x2="17" y2="21" />
        <line x1="12" y1="17" x2="12" y2="21" />
    </svg>
);

const FormatIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14,2 14,8 20,8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <polyline points="10,9 9,9 8,9" />
    </svg>
);

const GovernanceIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <path d="M12 8v4" />
        <path d="M12 16h.01" />
    </svg>
);

const LogoutIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
        <polyline points="16 17 21 12 16 7" />
        <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
);

const MenuIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <line x1="3" y1="6" x2="21" y2="6" />
        <line x1="3" y1="12" x2="21" y2="12" />
        <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
);

const CloseIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
);

function Layout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const toggleSidebar = () => {
        setSidebarOpen(!sidebarOpen);
    };

    const closeSidebar = () => {
        setSidebarOpen(false);
    };

    const primaryNav = [
        { to: '/app', icon: <DashboardIcon />, label: 'Dashboard', roles: ['admin', 'operator', 'viewer'] },
        { to: '/app/portal', icon: <PortalIcon />, label: 'Calculation Portal', roles: ['admin', 'operator', 'viewer'] },
        { to: '/app/governance', icon: <GovernanceIcon />, label: 'Governance', roles: ['admin'] },
        { to: '/app/formatting', icon: <FormatIcon />, label: 'Output Formatting', roles: ['admin'] },
    ];

    const filteredPrimary = primaryNav.filter(item => !item.roles || item.roles.includes(user?.role));

    return (
        <div className="layout">
            {/* Mobile Menu Button */}
            <button
                className="mobile-menu-btn"
                onClick={toggleSidebar}
                aria-label="Toggle navigation menu"
            >
                {sidebarOpen ? <CloseIcon /> : <MenuIcon />}
            </button>

            {/* Mobile Overlay */}
            <div
                className={`sidebar-overlay ${sidebarOpen ? 'open' : ''}`}
                onClick={closeSidebar}
            />

            {/* Sidebar */}
            <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
                <div className="sidebar-header">
                    <NavLink to="/app" className="logo" onClick={closeSidebar}>
                        <div className="logo-icon">
                            <svg width="32" height="32" viewBox="0 0 100 100" fill="none">
                                <defs>
                                    {/* Background gradient */}
                                    <linearGradient id="sidebarBgGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                        <stop offset="0%" stopColor="#22d3ee" />
                                        <stop offset="50%" stopColor="#3b82f6" />
                                        <stop offset="100%" stopColor="#8b5cf6" />
                                    </linearGradient>
                                    {/* Checkmark gradient */}
                                    <linearGradient id="sidebarCheckGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                        <stop offset="0%" stopColor="#06b6d4" />
                                        <stop offset="50%" stopColor="#3b82f6" />
                                        <stop offset="100%" stopColor="#a855f7" />
                                    </linearGradient>
                                </defs>

                                {/* Main container with rounded corners */}
                                <rect x="10" y="15" width="80" height="72" rx="12" ry="12" fill="url(#sidebarBgGradient)" />

                                {/* Grid cells - top row (filled) */}
                                <rect x="15" y="20" width="20" height="10" rx="2" fill="rgba(255,255,255,0.3)" />
                                <rect x="40" y="20" width="20" height="10" rx="2" fill="rgba(255,255,255,0.3)" />
                                <rect x="65" y="20" width="20" height="10" rx="2" fill="rgba(255,255,255,0.3)" />

                                {/* Grid cells - middle rows (white) */}
                                <rect x="15" y="34" width="20" height="10" rx="2" fill="white" />
                                <rect x="40" y="34" width="20" height="10" rx="2" fill="white" />
                                <rect x="65" y="34" width="20" height="10" rx="2" fill="white" />

                                <rect x="15" y="48" width="20" height="10" rx="2" fill="white" />
                                <rect x="40" y="48" width="20" height="10" rx="2" fill="white" />
                                <rect x="65" y="48" width="20" height="10" rx="2" fill="white" />

                                {/* Grid cells - bottom row (white) */}
                                <rect x="15" y="62" width="20" height="10" rx="2" fill="white" />
                                <rect x="40" y="62" width="20" height="10" rx="2" fill="white" />
                                <rect x="65" y="62" width="20" height="10" rx="2" fill="white" />

                                {/* Large checkmark */}
                                <path d="M50 53 L65 68 L95 30"
                                    stroke="url(#sidebarCheckGradient)"
                                    strokeWidth="8"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    fill="none" />
                            </svg>
                        </div>
                        <span className="logo-text">Spreadsheetr</span>
                    </NavLink>
                </div>

                <nav className="sidebar-nav">
                    <div className="nav-section">
                        {filteredPrimary.map(item => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.to === '/app'}
                                className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                                onClick={closeSidebar}
                            >
                                {item.icon}
                                <span>{item.label}</span>
                            </NavLink>
                        ))}
                    </div>
                </nav>

                <PlanStatus onNavigate={closeSidebar} />

                <div className="sidebar-footer">
                    <div className="user-info">
                        <div className="user-avatar" title={user?.username}>
                            {user?.username?.charAt(0)?.toUpperCase() || 'U'}
                        </div>
                        <div className="user-details">
                            <span className="user-name">{user?.username || 'User'}</span>
                            <span className="user-role">{user?.role || 'viewer'}</span>
                        </div>
                    </div>
                    <button
                        className="btn-logout tooltip"
                        onClick={handleLogout}
                        data-tooltip="Sign out"
                        aria-label="Sign out"
                    >
                        <LogoutIcon />
                    </button>
                </div>
            </aside>

            {/* Main Content */}
            <main className="main-content">
                <Outlet />
            </main>
        </div>
    );
}

export default Layout;
